// src/tabs/messages/segment-editor/SegmentEditor.jsx — Lot 1
//
// Éditeur SEGMENTÉ d'UN champ de message (body OU subject, pour une langue).
// La string stockée reste la source de vérité : on parse (Lot 0) en arbre,
// l'utilisateur manipule des textareas (runs de texte) + pastilles (variables)
// + encarts colorés (blocs {#if}) — puis on RE-SÉRIALISE à l'octet près.
//
// ── Invariants (contraintes pessimist) ──────────────────────────────────
// • IDENTITÉ : ouvrir puis enregistrer SANS modification ⇒ string byte-identique.
//   Garanti par serializeSegments (Lot 0) : runs verbatim + `node.open` verbatim.
//   → on ne commit (onChange) JAMAIS au montage, uniquement sur action utilisateur.
// • RE-SYNC : la valeur est parsée AU MONTAGE. Le parent force un remontage
//   (prop `key`) au changement de langue/champ → pas de useState non re-synchro.
// • NON round-trippable (nesting, {/if} orphelin, {#if} dans un sujet) → LECTURE
//   SEULE : on n'édite pas, on n'appelle jamais onChange → le save renvoie la
//   string d'origine intacte.
// • CARET : mémorisé PAR RUN sur onSelect/onBlur ({runId, start, end}), jamais
//   via document.activeElement (M2). Insertion au caret mémorisé.
// • SUJET : runs + pastilles uniquement, AUCUN encart {#if} (un \n dans un
//   header mail = header cassé, M1). Le picker sujet n'offre pas les blocs.
// • CUMUL {#if A ET B} (B4-ext) : OPT-IN via `allowCumul`, posé par le CORPS des
//   deux éditeurs (Direct et Plateformes). Sans la prop, le composant est
//   byte-pour-byte celui d'avant B4-ext. Quand elle est posée, éditer
//   l'EXPRESSION est la SEULE action qui réécrit `node.open` (forme canonique) :
//   sans ça, la string enregistrée garderait l'ancienne condition. Éditer le
//   CONTENU d'un encart cumulé laisse l'ouverture byte-identique.

import { Fragment, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SplitSquareVertical, X, ChevronDown } from "lucide-react";
import { DS } from "../../../lib/ui";
import {
  parseSegments,
  serializeSegments,
  isRoundTrippable,
  parseIfExpression,
  formatIfExpression,
} from "../../../lib/message-segments";
import {
  variableLabel,
  ifEncartMeta,
  BLOCK_MENU_GROUPS,
} from "./variable-labels";
import VarPickerButton from "./VarPickerButton";

let _seq = 0;
const uid = () => `seg-${++_seq}`;

// Ajoute un `_id` stable à chaque nœud (recursif). Ne mute pas l'arbre source.
function withIds(tree) {
  return (tree || []).map((n) => {
    if (n.type === "if")
      return { ...n, _id: uid(), children: withIds(n.children) };
    return { ...n, _id: uid() };
  });
}

// Initialise l'état depuis la string. Retourne { ok, tree } ou { ok:false }.
// `blocksForbidden` = les blocs {#if} ne sont pas éditables dans ce champ (sujet
// mono-ligne OU slug rendu hors moteur, ADR audit R4) : un {#if} DÉJÀ présent
// bascule alors en LECTURE SEULE (jamais un encart éditable trompeur sous le
// bandeau « rendu hors moteur »).
// `allowCumul` (B4-ext) : OPT-IN, sous-onglet Direct uniquement. false (défaut)
// → tokenisation HISTORIQUE : un cumul `{#if A ET B}` n'est pas une ouverture,
// son `{/if}` est orphelin ⇒ LECTURE SEULE, exactement comme avant B4-ext.
function initFromValue(value, blocksForbidden, allowCumul) {
  const opts = { allowCumul: !!allowCumul };
  if (!isRoundTrippable(value, opts)) return { ok: false };
  let tree;
  try {
    tree = parseSegments(value, opts);
  } catch (_) {
    return { ok: false };
  }
  if (blocksForbidden && tree.some((n) => n.type === "if"))
    return { ok: false };
  const withIdsTree = withIds(tree);
  // Champ vide → un run éditable pour accueillir la saisie (serialize '' === '').
  if (withIdsTree.length === 0)
    return { ok: true, tree: [{ type: "text", value: "", _id: uid() }] };
  return { ok: true, tree: normalizeForEdit(withIdsTree) };
}

// ── Normalisation d'édition (identité byte préservée) ───────────────────
// Deux garanties d'ergonomie SANS altérer la string sérialisée :
//   • `mergeAdjacentText` fusionne les runs de texte adjacents (récursif dans
//     les blocs) — concaténation ⇒ octets identiques ; conserve l'_id du 1er.
//   • `ensureEdgeRuns` garantit un run éditable en TÊTE et en QUEUE de la
//     racine (pour taper avant/après un token de bord : `{var}` seul, body
//     ouvrant sur `{#if}`) — runs vides ⇒ sérialisation '' ⇒ octets identiques.
// Idempotent : ré-appliqué à chaque commit sans accumulation.
function isTextNode(n) {
  return n && n.type === "text";
}
function mergeAdjacentText(nodes) {
  const out = [];
  for (const n of nodes) {
    if (n && n.type === "if") {
      out.push({ ...n, children: mergeAdjacentText(n.children) });
      continue;
    }
    const last = out[out.length - 1];
    if (isTextNode(n) && isTextNode(last)) {
      out[out.length - 1] = {
        ...last,
        value: (last.value || "") + (n.value || ""),
      };
    } else {
      out.push(n);
    }
  }
  return out;
}
function ensureEdgeRuns(nodes) {
  let out = nodes;
  if (out.length === 0) return [{ type: "text", value: "", _id: uid() }];
  if (!isTextNode(out[0]))
    out = [{ type: "text", value: "", _id: uid() }, ...out];
  if (!isTextNode(out[out.length - 1]))
    out = [...out, { type: "text", value: "", _id: uid() }];
  return out;
}
function normalizeForEdit(nodes) {
  return ensureEdgeRuns(mergeAdjacentText(nodes));
}

// ── Mutations d'arbre (pures, recursives) ───────────────────────────────

function removeNodeById(nodes, id) {
  const out = [];
  for (const n of nodes) {
    if (n._id === id) continue;
    if (n.type === "if")
      out.push({ ...n, children: removeNodeById(n.children, id) });
    else out.push(n);
  }
  return out;
}

// B4-ext — réécrit l'EXPRESSION d'un bloc {#if} (cumul ET/OU).
//
// ⚠️ POINT CRITIQUE : `open` DOIT être régénéré en forme canonique. serializeSegments
// donne la PRIORITÉ à `node.open` (verbatim d'origine) — le laisser tel quel
// ré-émettrait l'ANCIENNE condition alors que l'écran montre la nouvelle (le mail
// partirait avec la mauvaise condition). C'est le SEUL endroit où l'identité byte
// de l'ouverture est volontairement rompue, et uniquement sur action utilisateur
// explicite (ajout/retrait d'une branche, bascule ET/OU).
//
// Les blocs sont TOP-LEVEL uniquement (nesting interdit par le moteur) → pas de
// récursion nécessaire.
function updateIfExpressionById(nodes, id, operands, operator) {
  const expr = formatIfExpression(operands, operator);
  if (!expr) return nodes; // jamais d'expression vide (le moteur masquerait le bloc)
  return nodes.map((n) =>
    n._id === id && n.type === "if"
      ? { ...n, name: expr, open: `{#if ${expr}}` }
      : n,
  );
}

function updateRunById(nodes, id, newValue) {
  return nodes.map((n) => {
    if (n._id === id && n.type === "text") return { ...n, value: newValue };
    if (n.type === "if")
      return { ...n, children: updateRunById(n.children, id, newValue) };
    return n;
  });
}

// Insère `insertNodes` DANS le run `runId` au caret [start,end] (split du run).
// Le run « avant » conserve l'_id (continuité de focus), le « après » en reçoit un neuf.
function insertIntoRun(nodes, runId, start, end, insertNodes) {
  const out = [];
  let done = false;
  for (const n of nodes) {
    if (!done && n.type === "text" && n._id === runId) {
      const v = n.value || "";
      out.push({ type: "text", value: v.slice(0, start), _id: n._id });
      for (const ins of insertNodes) out.push(ins);
      out.push({ type: "text", value: v.slice(end), _id: uid() });
      done = true;
    } else if (!done && n.type === "if") {
      const res = insertIntoRun(n.children, runId, start, end, insertNodes);
      out.push({ ...n, children: res.nodes });
      if (res.done) done = true;
    } else {
      out.push(n);
    }
  }
  return { nodes: out, done };
}

// Fallback quand le caret est inconnu : append au bout de la racine + run vide.
function appendToRoot(root, insertNodes) {
  return [...root, ...insertNodes, { type: "text", value: "", _id: uid() }];
}

// Index racine de l'ancêtre TOP-LEVEL contenant le run `runId` (-1 si absent).
function topLevelIndexOfRun(root, runId) {
  for (let i = 0; i < root.length; i++) {
    const n = root[i];
    if (n._id === runId) return i;
    if (n.type === "if" && n.children.some((c) => c._id === runId)) return i;
  }
  return -1;
}

// ──────────────────────────────────────────────────────────────────────

export default function SegmentEditor({
  value,
  onChange,
  variant = "body", // 'body' | 'subject'
  varGroups, // groupes du picker (null → catalogue complet côté parent)
  allowBlocks = true, // faux pour sujet ET slugs « rendus hors moteur »
  // M12 (Direct) : résolveur de libellé OPTIONNEL (name → label) consulté en
  // priorité pour les pastilles ET le picker — le sous-onglet Direct a son
  // propre dico (VAR_LABELS_FR), distinct du catalogue Plateformes. Absent
  // (Plateformes) : comportement historique inchangé (variableLabel /
  // humanPickerLabel).
  labelFor = null,
  // B4 (Direct) : menu « + Paragraphe conditionnel » PARAMÉTRABLE. Absent
  // (Plateformes) : on garde BLOCK_MENU_GROUPS (is_airbnb/is_booking/caution_*).
  // Le sous-onglet Direct passe SES conditions (catalogue B2 : caution_empreinte,
  // formulaire_manquant…) via DIRECT_CONDITION_BLOCK_GROUPS.
  blockGroups = BLOCK_MENU_GROUPS,
  // B4 (Direct) : meta d'encart {#if} OPTIONNELLE consultée AVANT ifEncartMeta —
  // permet au Direct d'afficher ses propres bandeaux « SEULEMENT SI … » (les
  // conditions Direct sont hors du référentiel Plateformes d'ifEncartMeta). Un
  // nom inconnu de ce résolveur → fallback ifEncartMeta habituel (générique).
  blockMetaFor = null,
  // B4-ext : cumul de conditions dans un encart ({#if A ET B}).
  // ⚠️ OPT-IN — posé par le CORPS des deux éditeurs (Direct et Plateformes),
  // décision Dimitri 2026-07-28 : le message de PROD `demande_caution_formulaire`
  // (« Rappel J-2 unifié ») contient 3 cumuls et basculait en lecture seule,
  // devenant NON MODIFIABLE depuis l'admin.
  //   • false (DÉFAUT) → tokenisation HISTORIQUE. Un `{#if}` simple se comporte
  //     au caractère près comme avant B4-ext (aucune puce, aucun sélecteur
  //     ET/OU, aucun « + condition »), et un cumul reste en LECTURE SEULE.
  //   • true → le cumul est round-trippé et l'encart propose les puces par
  //     branche, le sélecteur ET/OU et « + condition ».
  // Le DÉFAUT reste `false` volontairement : il borne le blast-radius de
  // l'inversion R2 aux champs explicitement ouverts (l'OBJET, par exemple, ne
  // l'active pas — un {#if} y est de toute façon interdit).
  allowCumul = false,
  // M12+ (Direct) : le picker « + Info voyageur » est rendu par le PARENT (un
  // seul, partagé entre Objet et Corps). false → ce champ n'affiche PAS son
  // propre bouton ; le parent insère via apiRef.current.insertVariable. Défaut
  // true → Plateformes/Direct historique inchangés (byte-identiques).
  showVarPicker = true,
  // Notifie le parent que ce champ devient actif (focus/caret dans un run) → le
  // bouton partagé cible ce champ.
  onFieldFocus = null,
  // Ref-objet peuplée avec { insertVariable, readOnly } → le parent pilote
  // l'insertion dans le champ actif. Rafraîchie à chaque rendu.
  apiRef = null,
  // M2 : notifie le parent de l'état lecture-seule de ce champ (contenu non
  // round-trippable) → le bouton partagé peut se griser si les 2 champs le sont.
  onReadOnly = null,
}) {
  const isSubject = variant === "subject";
  // Blocs {#if} interdits dans un sujet (mono-ligne) OU un slug rendu hors moteur
  // (allowBlocks=false) : un {#if} déjà présent ⇒ lecture seule (pas d'encart
  // éditable trompeur). Cf. audit R4.
  const blocksForbidden = isSubject || !allowBlocks;
  // Montage = source de parse. Le parent remonte (key) au switch langue/champ.
  const init = useMemo(
    () => initFromValue(value, blocksForbidden, allowCumul),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [segments, setSegments] = useState(() => (init.ok ? init.tree : []));
  const lastCaret = useRef(null); // { runId, start, end }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [blockMenuOpen, setBlockMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const readOnly = !init.ok;

  // Toute mutation passe par commit → set state + remontée string (jamais au
  // montage). On NORMALISE (fusion runs adjacents + runs de bord éditables) : la
  // string sérialisée reste octet-identique au contenu réel, seule l'ergonomie
  // d'édition change.
  function commit(next) {
    const norm = normalizeForEdit(next);
    setSegments(norm);
    onChange(serializeSegments(norm));
  }

  function onRunChange(id, newValue) {
    commit(updateRunById(segments, id, newValue));
  }
  function onCaret(id, el) {
    lastCaret.current = {
      runId: id,
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
    };
    if (onFieldFocus) onFieldFocus();
  }
  function removeNode(id) {
    setConfirmDeleteId(null);
    commit(removeNodeById(segments, id));
  }
  // B4-ext : l'utilisateur a modifié l'expression d'un bloc (ajout/retrait d'une
  // branche, bascule ET/OU) → réécriture canonique de l'ouverture.
  function updateIfExpression(id, operands, operator) {
    commit(updateIfExpressionById(segments, id, operands, operator));
  }

  function insertVariable(name) {
    if (readOnly) return;
    const pill = { type: "var", name, _id: uid() };
    const c = lastCaret.current;
    if (c) {
      const res = insertIntoRun(segments, c.runId, c.start, c.end, [pill]);
      if (res.done) return commit(res.nodes);
    }
    commit(appendToRoot(segments, [pill]));
  }

  function insertBlock(name) {
    setBlockMenuOpen(false);
    const node = {
      type: "if",
      name,
      open: `{#if ${name}}`,
      children: [{ type: "text", value: "", _id: uid() }],
      _id: uid(),
    };
    const c = lastCaret.current;
    const idx = c ? topLevelIndexOfRun(segments, c.runId) : -1;
    if (idx < 0) return commit([...segments, node]);
    const next = [...segments];
    next.splice(idx + 1, 0, node); // TOP-LEVEL uniquement (nesting interdit)
    commit(next);
  }

  // Délégation de clic sur le FOND (surface d'édition ou encart {#if}) : un
  // mousedown qui atterrit sur un élément marqué data-seg-bg — jamais une
  // pastille, un bouton ✕/Oui/Non ni une textarea — focus la textarea de run
  // la plus proche du point cliqué (priorité verticale : le flux est en
  // lignes), caret posé au bord pertinent. Un clic « à côté » ne tombe plus
  // dans le vide (encart fraîchement créé, marge droite après une pastille…).
  // AUCUN commit : le modèle et la string stockée ne changent pas.
  function onSurfaceMouseDown(e) {
    if (e.button !== 0) return; // clic principal uniquement (pas le menu contextuel)
    const t = e.target;
    if (!t || !t.dataset || t.dataset.segBg === undefined) return;
    const tas = Array.from(
      e.currentTarget.querySelectorAll("textarea[data-run-id]"),
    );
    if (tas.length === 0) return;
    const x = e.clientX;
    const y = e.clientY;
    let best = null;
    let bestScore = Infinity;
    for (const ta of tas) {
      const r = ta.getBoundingClientRect();
      const dy = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
      const dx = x < r.left ? r.left - x : x > r.right ? x - r.right : 0;
      const score = dy * 10000 + dx; // même ligne d'abord, puis proximité horizontale
      if (score < bestScore) {
        bestScore = score;
        best = { ta, rect: r };
      }
    }
    e.preventDefault(); // le fond ne vole pas le focus qu'on vient de donner
    const el = best.ta;
    const pos = x < best.rect.left ? 0 : el.value.length;
    el.focus();
    el.setSelectionRange(pos, pos);
    // Mémorise le caret (coordonnées PLEIN RUN) : une insertion au picker qui
    // suit ce clic vise l'endroit cliqué, pas un caret périmé.
    lastCaret.current = {
      runId: el.dataset.runId,
      start: Number(el.dataset.lineBase || 0) + pos,
      end: Number(el.dataset.lineBase || 0) + pos,
    };
    if (onFieldFocus) onFieldFocus();
  }

  // Expose une API impérative au parent (Direct M12+ : bouton « + Info voyageur »
  // unique partagé → insertion dans le CHAMP ACTIF). apiRef.current est rafraîchi
  // à CHAQUE rendu : insertVariable ferme sur les segments/caret courants ;
  // `readOnly` permet au parent de ne pas cibler un champ non éditable.
  useLayoutEffect(() => {
    if (apiRef) apiRef.current = { insertVariable, readOnly };
    if (onReadOnly) onReadOnly(readOnly);
  });

  if (readOnly) {
    return (
      <div>
        <div
          data-testid="segment-readonly"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: "8px 11px",
            marginBottom: 6,
            background: DS.warningBg,
            border: `0.5px solid ${DS.warning}`,
            borderRadius: 8,
            fontSize: 11.5,
            color: DS.warningText,
            lineHeight: 1.5,
          }}
        >
          <span>
            Ce texte est trop complexe pour l'éditeur par blocs : il est affiché
            brut et sera enregistré <strong>exactement tel quel</strong>.
          </span>
        </div>
        <textarea
          readOnly
          value={value || ""}
          rows={isSubject ? 2 : 8}
          style={{
            width: "100%",
            border: `0.5px solid ${DS.border}`,
            borderRadius: 9,
            padding: "10px 12px",
            fontSize: 12,
            background: DS.pageBg,
            fontFamily: "monospace",
            lineHeight: 1.5,
            resize: "vertical",
            color: DS.textSecondary,
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar d'insertion (par surface) */}
      <div
        style={{
          display: "flex",
          gap: 7,
          marginBottom: 6,
          flexWrap: "wrap",
          position: "relative",
        }}
      >
        {/* Picker « + Info voyageur » PAR CHAMP (Plateformes + Direct historique).
            Direct M12+ passe showVarPicker=false : le picker est rendu UNE fois
            dans DirectMessagesView, au-dessus des deux champs, et insère dans le
            champ actif via la ref (insertVariable). */}
        {showVarPicker && (
          <VarPickerButton
            varGroups={varGroups}
            labelFor={labelFor}
            onPick={insertVariable}
            open={pickerOpen}
            onOpenChange={(v) => {
              setPickerOpen(v);
              if (v) setBlockMenuOpen(false);
            }}
          />
        )}

        {allowBlocks && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="fbz-press"
              onClick={() => {
                setBlockMenuOpen((o) => !o);
                setPickerOpen(false);
              }}
              style={ghostBtn}
            >
              <SplitSquareVertical size={13} strokeWidth={2} /> + Paragraphe
              conditionnel
              <ChevronDown size={12} strokeWidth={2} />
            </button>
            {blockMenuOpen && (
              <div style={popover}>
                {/* Menu GROUPÉ (POINT 4) : Plateformes / Conditions — même
                    pattern d'en-têtes que le picker « + Info voyageur ». B4 :
                    `blockGroups` peut être surchargé (conditions Direct). */}
                {blockGroups.map((group) => (
                  <div key={group.category} style={{ marginBottom: 6 }}>
                    <div
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: DS.marine,
                        padding: "3px 8px",
                      }}
                    >
                      {group.category}
                    </div>
                    {group.options.map((o) => (
                      <button
                        key={o.name}
                        type="button"
                        onClick={() => insertBlock(o.name)}
                        style={popoverItem}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = DS.pageBg)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Surface segmentée */}
      <div
        data-seg-bg=""
        onMouseDown={onSurfaceMouseDown}
        style={{
          cursor: "text",
          border: `0.5px solid ${DS.border}`,
          borderRadius: 9,
          padding: "11px 13px",
          background: "#fdfdfe",
          fontSize: 13.5,
          lineHeight: 1.9,
          minHeight: isSubject ? 40 : 90,
        }}
      >
        <SegmentNodes
          nodes={segments}
          onRunChange={onRunChange}
          onCaret={onCaret}
          removeNode={removeNode}
          confirmDeleteId={confirmDeleteId}
          setConfirmDeleteId={setConfirmDeleteId}
          labelFor={labelFor}
          blockMetaFor={blockMetaFor}
          updateIfExpression={updateIfExpression}
          blockGroups={blockGroups}
          allowCumul={allowCumul}
          singleLine={isSubject}
        />
      </div>
    </div>
  );
}

// Rendu récursif d'une liste de segments.
// `startsLine`/`endsLine` (calculés ici, par position) disent à RunTextarea si
// le run commence/termine sa ligne de flux : un voisin `if` est un <div> bloc
// (rupture de ligne), l'absence de voisin aussi. Sert au fix « ligne vide
// invisible » (une ligne vide SEULE sur sa ligne peut s'étirer sans jamais
// pousser une pastille voisine).
// `inEncart` : la liste est le contenu d'un IfEncart → les runs de BORD
// (1er / dernier enfant) compactent leurs lignes vides de bord (hauteur auto
// de l'encart, cf. doc RunTextarea). Jamais posé à la racine.
function SegmentNodes({
  nodes,
  onRunChange,
  onCaret,
  removeNode,
  confirmDeleteId,
  setConfirmDeleteId,
  inEncart = false,
  labelFor = null,
  blockMetaFor = null,
  updateIfExpression = null,
  blockGroups = BLOCK_MENU_GROUPS,
  allowCumul = false,
  singleLine = false,
}) {
  const breaksLine = (sibling) => !sibling || sibling.type === "if";
  return (
    <>
      {nodes.map((n, idx) => {
        if (n.type === "text")
          return (
            <RunTextarea
              key={n._id}
              node={n}
              onRunChange={onRunChange}
              onCaret={onCaret}
              startsLine={breaksLine(nodes[idx - 1])}
              endsLine={breaksLine(nodes[idx + 1])}
              singleLine={singleLine}
              encartEdge={
                inEncart
                  ? { first: idx === 0, last: idx === nodes.length - 1 }
                  : null
              }
            />
          );
        if (n.type === "var")
          return (
            <VarPill
              key={n._id}
              node={n}
              onRemove={removeNode}
              labelFor={labelFor}
            />
          );
        if (n.type === "if")
          return (
            <IfEncart
              key={n._id}
              node={n}
              onRunChange={onRunChange}
              onCaret={onCaret}
              removeNode={removeNode}
              confirmDeleteId={confirmDeleteId}
              setConfirmDeleteId={setConfirmDeleteId}
              labelFor={labelFor}
              blockMetaFor={blockMetaFor}
              updateIfExpression={updateIfExpression}
              blockGroups={blockGroups}
              allowCumul={allowCumul}
            />
          );
        return null;
      })}
    </>
  );
}

// Run de texte — rendu PAR LIGNE VISUELLE (fix mise en page, maquette
// docs/handoff/maquette-editeur-template-final.html).
//
// AVANT : un run = UNE boîte atomique inline-grid (miroir + textarea). Un run
// contenant des `\n` (« €\nSource : ») devenait un RECTANGLE multi-ligne
// atomique : ses `\n` ne cassaient jamais la ligne du flux EXTERNE, et les
// pastilles voisines s'alignaient À CÔTÉ du rectangle (sur la baseline de sa
// 1re ligne) → libellés flottants / grands vides (bug prod visible sur
// notif_admin_resa_annulee).
//
// APRÈS : le run est rendu ligne par ligne — chaque `\n` devient un <br> du
// flux inline externe, chaque ligne est un textarea auto-grow mono-ligne →
// « Libellé : [pastille] » coule inline, ligne par ligne, comme la maquette.
//
// La VALEUR du run reste LA source unique (arbre de segments) : éditer une
// sous-ligne reconstruit la valeur complète (jointure "\n") → serializeSegments
// inchangé, byte-identique. Les offsets caret remontés via onCaret sont en
// coordonnées PLEIN RUN (base de ligne + offset local) → insertIntoRun intact.
const runMirrorStyle = {
  gridArea: "1 / 1",
  visibility: "hidden",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: DS.fontBody,
  fontSize: 13.5,
  lineHeight: 1.9,
  minWidth: 8,
};
const runTextareaStyle = {
  gridArea: "1 / 1",
  fontFamily: DS.fontBody,
  fontSize: 13.5,
  lineHeight: 1.9,
  border: "none",
  outline: "none",
  background: "transparent",
  resize: "none",
  overflow: "hidden",
  padding: 0,
  margin: 0,
  color: DS.marineDark,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
// ── Hauteur AUTO des encarts {#if} : lignes vides de BORD compactées ─────
//
// Constat Dimitri : un bloc conditionnel au petit contenu rendait une GRANDE
// boîte pleine de vide. Cause : les \n de bord du bloc (« {#if x}\ncontenu\n\n{/if} »)
// se rendaient chacun en PLEINE ligne vide (~26 px, lineHeight 1.9) dans
// l'encart — 1 ligne utile = 3+ lignes affichées, quel que soit le contenu.
//
// Fix : dans un encart, une ligne vide de BORD (avant le 1er contenu du
// 1er run enfant / après le dernier contenu du dernier run enfant) se rend
// en fine bande cliquable de 8 px au lieu d'une pleine ligne → la hauteur du
// bloc suit le nombre de lignes RÉELLES, au montage comme à chaque re-rendu
// (frappe, insertion : le rendu dérive de la valeur). Dès qu'une bande
// reçoit du texte, sa ligne redevient pleine hauteur au rendu suivant.
//
// Les lignes vides INTÉRIEURES (saut de paragraphe voulu dans le bloc) et
// les lignes vides HORS encart (séparations de paragraphes du corps) gardent
// leur pleine hauteur : ce sont de vraies lignes du message final.
//
// AUCUN impact sérialisation : rendu seul — valeur du run, offsets caret
// (data-line-base) et handlers strictement identiques ; la bande reste
// focusable et participe à la délégation de clic comme toute sous-ligne.
const COMPACT_EDGE_H = 8;
const compactLineStyle = {
  display: "block",
  width: "100%",
  height: COMPACT_EDGE_H,
};
const compactRunTextareaStyle = {
  ...runTextareaStyle,
  display: "block",
  width: "100%",
  height: COMPACT_EDGE_H,
  fontSize: COMPACT_EDGE_H,
  lineHeight: `${COMPACT_EDGE_H}px`,
};
function RunTextarea({
  node,
  onRunChange,
  onCaret,
  startsLine,
  endsLine,
  encartEdge,
  singleLine = false,
}) {
  const val = node.value || "";
  const lines = val.split("\n");
  // Offset absolu (coordonnées PLEIN RUN) du début de chaque ligne.
  const bases = [];
  for (let i = 0, acc = 0; i < lines.length; i++) {
    bases.push(acc);
    acc += lines[i].length + 1; // +1 = le \n séparateur
  }

  const elsRef = useRef({}); // index de ligne → élément textarea monté
  // Caret à restaurer (offset PLEIN RUN) après une édition qui CHANGE le nombre
  // de lignes (Entrée, collage multi-ligne, fusion Backspace/Suppr) : les
  // textareas sont recréés → le caret natif est perdu, on le repose ici.
  const pendingCaretRef = useRef(null);
  useLayoutEffect(() => {
    const pos = pendingCaretRef.current;
    if (pos == null) return;
    pendingCaretRef.current = null;
    for (let i = 0; i < lines.length; i++) {
      const isLast = i === lines.length - 1;
      if (pos <= bases[i] + lines[i].length || isLast) {
        const el = elsRef.current[i];
        if (el) {
          const off = Math.max(0, Math.min(pos - bases[i], lines[i].length));
          el.focus();
          el.setSelectionRange(off, off);
          // Re-synchronise le caret mémorisé du parent (coordonnées PLEIN RUN).
          onCaret(node._id, {
            selectionStart: bases[i] + off,
            selectionEnd: bases[i] + off,
          });
        }
        return;
      }
    }
  });

  // Shim onCaret : traduit la sélection LOCALE d'une sous-ligne en offsets
  // PLEIN RUN (le parent ne connaît que {runId, start, end} sur le run entier).
  function caretShim(i, el) {
    return {
      selectionStart: bases[i] + (el.selectionStart ?? 0),
      selectionEnd: bases[i] + (el.selectionEnd ?? 0),
    };
  }

  function commitLines(nextLines, caretAbs) {
    if (caretAbs != null) pendingCaretRef.current = caretAbs;
    onRunChange(node._id, nextLines.join("\n"));
  }

  function onLineChange(i, el) {
    let nv = el.value;
    // Garde MONO-LIGNE (fix pessimist #997) : dans un SUJET, un \n est
    // interdit — l'ancien <input> le rendait physiquement impossible, et un
    // \n dans un header Subject casse l'email (Direct transactionnel NON gaté
    // DRY_RUN → partirait en réel). La frappe Entrée est bloquée en amont
    // (onLineKeyDown) ; ici on neutralise le COLLAGE / l'insertion
    // programmatique multi-ligne : \r\n → espace simple.
    if (singleLine && /[\r\n]/.test(nv)) nv = nv.replace(/[\r\n]+/g, " ");
    const next = [...lines];
    next[i] = nv;
    // Sous-ligne contenant un \n (Entrée / collage multi-ligne) → le nombre de
    // lignes change et l'élément focus sera recréé : caret à restaurer.
    const caretAbs = nv.includes("\n")
      ? bases[i] + (el.selectionStart ?? nv.length)
      : null;
    commitLines(next, caretAbs);
  }

  // Fusion de lignes au clavier (le \n séparateur vit ENTRE deux textareas) :
  // Backspace en tête de ligne / Suppr en fin de ligne le retirent.
  function onLineKeyDown(i, e) {
    // Garde MONO-LIGNE (fix pessimist #997) : Entrée dans un sujet = no-op —
    // jamais de \n inséré (cf. onLineChange pour le collage).
    if (singleLine && e.key === "Enter") {
      e.preventDefault();
      return;
    }
    const el = e.target;
    const collapsed = el.selectionStart === el.selectionEnd;
    if (!collapsed) return;
    if (e.key === "Backspace" && el.selectionStart === 0 && i > 0) {
      e.preventDefault();
      const next = [...lines];
      next.splice(i - 1, 2, lines[i - 1] + lines[i]);
      commitLines(next, bases[i] - 1);
    } else if (
      e.key === "Delete" &&
      el.selectionStart === lines[i].length &&
      i < lines.length - 1
    ) {
      e.preventDefault();
      const next = [...lines];
      next.splice(i, 2, lines[i] + lines[i + 1]);
      commitLines(next, bases[i] + lines[i].length);
    }
  }

  // Ligne vide SEULE sur sa ligne de flux (rien d'autre ne la partage :
  // bordée par des sauts de ligne du run, un bord de bloc ou rien) → elle
  // s'étire sur toute la largeur disponible pour rester VISIBLE et CLIQUABLE
  // (fix « encart au grand vide » : un run/une ligne vide se rendait en
  // textarea de ~8 px impossible à viser). Une ligne vide qui PARTAGE sa
  // ligne (ex. run de bord collé à une pastille) garde sa largeur mini :
  // s'étirer pousserait la pastille à la ligne (régression de mise en page).
  const isStandaloneEmpty = (i) =>
    lines[i] === "" &&
    (i > 0 || startsLine) &&
    (i < lines.length - 1 || endsLine);

  // Bornes de contenu du run (1re / dernière ligne NON vide) — délimitent
  // les lignes vides de BORD compactables dans un encart (hauteur auto).
  let firstContent = -1;
  let lastContent = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] !== "") {
      if (firstContent === -1) firstContent = i;
      lastContent = i;
    }
  }
  const isCompact = (i) => {
    if (!encartEdge || !isStandaloneEmpty(i)) return false;
    if (firstContent === -1) {
      // Run entièrement vide. Seul enfant de l'encart (bord des deux côtés) :
      // garder la 1re ligne pleine hauteur — c'est l'affordance de saisie d'un
      // encart neuf/vidé. Sinon (le contenu vit dans un frère) : tout compacter.
      return encartEdge.first && encartEdge.last ? i > 0 : true;
    }
    return (
      (encartEdge.first && i < firstContent) ||
      (encartEdge.last && i > lastContent)
    );
  };

  return (
    <>
      {lines.map((line, i) => {
        const standaloneEmpty = isStandaloneEmpty(i);
        // Ligne vide de BORD d'encart → fine bande bloc cliquable (hauteur
        // auto du bloc, cf. doc au-dessus du composant). Un display:block
        // casse la ligne de lui-même : aucun <br> autour (cf. condition plus
        // bas). Mêmes ref/handlers/data-attrs qu'une ligne normale : caret,
        // délégation de clic et re-sérialisation strictement inchangés.
        if (isCompact(i)) {
          return (
            <span key={i} data-seg-compact="" style={compactLineStyle}>
              <textarea
                ref={(el) => {
                  if (el) elsRef.current[i] = el;
                  else delete elsRef.current[i];
                }}
                value={line}
                rows={1}
                cols={1}
                data-run-id={node._id}
                data-line-base={bases[i]}
                onChange={(e) => onLineChange(i, e.target)}
                onKeyDown={(e) => onLineKeyDown(i, e)}
                onSelect={(e) => onCaret(node._id, caretShim(i, e.target))}
                onKeyUp={(e) => onCaret(node._id, caretShim(i, e.target))}
                onClick={(e) => onCaret(node._id, caretShim(i, e.target))}
                onBlur={(e) => onCaret(node._id, caretShim(i, e.target))}
                style={compactRunTextareaStyle}
              />
            </span>
          );
        }
        return (
          <Fragment key={i}>
            {/* Le \n du run = un VRAI retour à la ligne du flux inline externe. */}
            {i > 0 && !isCompact(i - 1) && <br />}
            <span
              style={{
                display: "inline-grid",
                verticalAlign: "top",
                maxWidth: "100%",
                // La piste grid suit le miroir (min-content) : sans largeur
                // imposée, une ligne vide resterait à ~8 px même avec width:100%.
                ...(standaloneEmpty
                  ? { width: "100%", gridTemplateColumns: "minmax(8px, 1fr)" }
                  : {}),
              }}
            >
              {/* Miroir invisible → dimensionne le grid (grow-wrap). ZWSP largeur mini. */}
              <span aria-hidden="true" style={runMirrorStyle}>
                {line + "\u200b"}
              </span>
              <textarea
                ref={(el) => {
                  if (el) elsRef.current[i] = el;
                  else delete elsRef.current[i];
                }}
                value={line}
                rows={1}
                // cols par défaut (20) → largeur MIN-CONTENT ~160px imposée à la
                // piste grid : chaque run court (« Voyageur : ») gonflait à
                // ~160px et poussait sa pastille loin à droite (les « grands
                // vides » du bug). cols=1 → la piste est dimensionnée par le
                // MIROIR seul (largeur au contenu réel).
                cols={1}
                // Identifiants DOM pour la délégation de clic (fond de surface /
                // d'encart → focus du run le plus proche, offsets PLEIN RUN).
                data-run-id={node._id}
                data-line-base={bases[i]}
                onChange={(e) => onLineChange(i, e.target)}
                onKeyDown={(e) => onLineKeyDown(i, e)}
                onSelect={(e) => onCaret(node._id, caretShim(i, e.target))}
                onKeyUp={(e) => onCaret(node._id, caretShim(i, e.target))}
                onClick={(e) => onCaret(node._id, caretShim(i, e.target))}
                onBlur={(e) => onCaret(node._id, caretShim(i, e.target))}
                style={runTextareaStyle}
              />
            </span>
          </Fragment>
        );
      })}
    </>
  );
}

// Pastille ambre atomique (variable). Label humain, ✕ pour retirer.
// `labelFor` (M12) : dico du parent consulté en priorité (Direct) — absent ou
// muet sur ce nom → fallback historique variableLabel (jamais vide : une
// variable inconnue reste identifiable par son nom brut, pastille neutre).
function VarPill({ node, onRemove, labelFor }) {
  const label = (labelFor && labelFor(node.name)) || variableLabel(node.name);
  return (
    <span
      data-seg-var={node.name}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "#FAEEDA",
        color: "#854F0B",
        fontWeight: 600,
        padding: "1px 4px 1px 9px",
        borderRadius: 6,
        border: "0.5px solid #f0dca8",
        fontSize: 12.5,
        margin: "0 1px",
        verticalAlign: "baseline",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <button
        type="button"
        onClick={() => onRemove(node._id)}
        aria-label={`Retirer la variable ${label}`}
        style={pillRemoveBtn("#854F0B")}
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </span>
  );
}

// Encart coloré d'un bloc {#if} (plateforme = couleur dédiée ; sinon ambre).
// ✕ retire l'encart ET son contenu, après confirmation inline légère.
//
// B4-ext — CUMUL : l'expression peut porter plusieurs conditions (`a ET b`).
// L'en-tête affiche alors une PUCE par opérande (✕ = retirer cette branche) et
// un sélecteur ET/OU. « + condition » ajoute une branche depuis le MÊME menu que
// « + Paragraphe conditionnel » (prop blockGroups). Une expression que le moteur
// ne sait pas évaluer (mélange ET/OU, arité paire…) est signalée telle quelle et
// n'est JAMAIS réparée automatiquement — seul le CONTENU reste éditable.
function IfEncart({
  node,
  onRunChange,
  onCaret,
  removeNode,
  confirmDeleteId,
  setConfirmDeleteId,
  labelFor = null,
  blockMetaFor = null,
  updateIfExpression = null,
  blockGroups = BLOCK_MENU_GROUPS,
  allowCumul = false,
}) {
  // B4 : meta Direct (bandeau « SEULEMENT SI … ») consultée en priorité ; à
  // défaut, fallback historique ifEncartMeta (plateforme / condition / générique
  // / cumul / expression non supportée).
  const meta =
    (blockMetaFor && blockMetaFor(node.name)) || ifEncartMeta(node.name);
  const confirming = confirmDeleteId === node._id;
  const expr = parseIfExpression(node.name);
  const [addOpen, setAddOpen] = useState(false);
  // Opérateur choisi pour la PROCHAINE branche quand le bloc n'en a encore
  // qu'une. Dès qu'il y en a ≥ 2, l'opérateur en place fait loi (le moteur
  // interdit le mélange ET/OU → l'expression serait masquée).
  const [pendingOp, setPendingOp] = useState("ET");
  const operator = expr.operator || pendingOp;
  // Édition d'expression disponible seulement si l'expression est SAINE (on ne
  // réécrit jamais une expression qu'on ne comprend pas) et si le parent a
  // fourni le handler.
  const canEditExpr = allowCumul && expr.valid && !!updateIfExpression;
  const isCumul = expr.operands.length > 1;

  // Libellé COURT d'une branche (puce) : dico du parent (Direct) en priorité,
  // sinon le libellé humain historique. Jamais le bandeau long « SEULEMENT SI … ».
  function labelOf(name) {
    return (labelFor && labelFor(name)) || variableLabel(name);
  }
  function addOperand(name) {
    setAddOpen(false);
    // Anti-doublon : `{#if a ET a}` est légal pour le moteur mais absurde.
    if (expr.operands.includes(name)) return;
    updateIfExpression(node._id, [...expr.operands, name], operator);
  }
  function removeOperand(name) {
    const next = expr.operands.filter((o) => o !== name);
    if (next.length === 0) return; // jamais d'expression vide (cf. ✕ du bloc)
    updateIfExpression(node._id, next, next.length > 1 ? operator : null);
  }
  function switchOperator(op) {
    setPendingOp(op);
    if (isCumul) updateIfExpression(node._id, expr.operands, op);
  }
  return (
    // data-seg-bg : le fond de l'encart participe à la délégation de clic de la
    // surface (mousedown → focus du run le plus proche). Les pastilles, boutons
    // ✕/Oui/Non et la barre de titre n'ont PAS l'attribut → jamais volés.
    <div
      data-seg-bg=""
      style={{
        // Lot visuel n°2 (Dimitri) : encart COMPACT — réduit à la taille de
        // son contenu (paddings/marges/bandeau resserrés), label + ✕ conservés.
        border: `1.5px solid ${meta.border}`,
        background: meta.bg,
        borderRadius: 8,
        padding: "4px 9px",
        margin: "4px 0",
        lineHeight: 1.5,
        cursor: "text",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 2,
        }}
      >
        <span
          style={{
            fontSize: 9,
            letterSpacing: 0.6,
            fontWeight: 700,
            color: meta.text,
          }}
        >
          {meta.label}
        </span>
        {/* B4-ext — commandes de cumul : puces par branche (✕), sélecteur ET/OU,
            « + condition ». Masquées si l'expression n'est pas évaluable par le
            moteur (variant 'invalid') : on ne propose pas d'y greffer une
            branche de plus. */}
        {canEditExpr && (
          <span
            data-seg-cumul=""
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              flexWrap: "wrap",
              marginRight: "auto",
            }}
          >
            {isCumul &&
              expr.operands.map((op) => (
                <span key={op} data-seg-operand={op} style={operandChip}>
                  {labelOf(op)}
                  <button
                    type="button"
                    onClick={() => removeOperand(op)}
                    aria-label={`Retirer la condition ${labelOf(op)}`}
                    style={pillRemoveBtn(meta.text)}
                  >
                    <X size={10} strokeWidth={2.4} />
                  </button>
                </span>
              ))}
            {/* Sélecteur ET/OU : visible dès qu'il y a ≥ 2 branches (le moteur
                interdit le MÉLANGE → basculer réécrit TOUTE l'expression). Sur
                une condition simple, le choix se fait dans le menu d'ajout. */}
            {isCumul && (
              <span style={{ display: "inline-flex", gap: 2 }}>
                {["ET", "OU"].map((op) => (
                  <button
                    key={op}
                    type="button"
                    onClick={() => switchOperator(op)}
                    aria-pressed={operator === op}
                    aria-label={`Cumuler avec ${op}`}
                    style={opToggleBtn(meta.text, operator === op)}
                  >
                    {op}
                  </button>
                ))}
              </span>
            )}
            <span style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setAddOpen((o) => !o)}
                style={addCondBtn(meta.text)}
              >
                + condition
              </button>
              {addOpen && (
                <div style={{ ...popover, top: 22 }}>
                  {!isCumul && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 8px 6px",
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: DS.marine,
                      }}
                    >
                      Cumuler avec
                      {["ET", "OU"].map((op) => (
                        <button
                          key={op}
                          type="button"
                          onClick={() => switchOperator(op)}
                          aria-pressed={operator === op}
                          aria-label={`Cumuler avec ${op}`}
                          style={opToggleBtn(meta.text, operator === op)}
                        >
                          {op}
                        </button>
                      ))}
                    </div>
                  )}
                  {blockGroups.map((group) => (
                    <div key={group.category} style={{ marginBottom: 6 }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: DS.marine,
                          padding: "3px 8px",
                        }}
                      >
                        {group.category}
                      </div>
                      {group.options.map((o) => (
                        <button
                          key={o.name}
                          type="button"
                          disabled={expr.operands.includes(o.name)}
                          onClick={() => addOperand(o.name)}
                          style={{
                            ...popoverItem,
                            opacity: expr.operands.includes(o.name) ? 0.4 : 1,
                            cursor: expr.operands.includes(o.name)
                              ? "default"
                              : "pointer",
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </span>
          </span>
        )}
        {confirming ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: meta.text,
            }}
          >
            Supprimer ce bloc ?
            <button
              type="button"
              onClick={() => removeNode(node._id)}
              style={confirmBtn(meta.text, true)}
            >
              Oui
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeleteId(null)}
              style={confirmBtn(meta.text, false)}
            >
              Non
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDeleteId(node._id)}
            aria-label={`Retirer le bloc ${meta.label}`}
            style={pillRemoveBtn(meta.text)}
          >
            <X size={12} strokeWidth={2.2} />
          </button>
        )}
      </div>
      <div data-seg-bg="" style={{ color: meta.text }}>
        <SegmentNodes
          nodes={node.children}
          onRunChange={onRunChange}
          onCaret={onCaret}
          removeNode={removeNode}
          confirmDeleteId={confirmDeleteId}
          setConfirmDeleteId={setConfirmDeleteId}
          inEncart
          labelFor={labelFor}
          blockMetaFor={blockMetaFor}
        />
      </div>
    </div>
  );
}

// ── styles partagés ─────────────────────────────────────────────────────
const ghostBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: 8,
  padding: "4px 11px",
  background: DS.card,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};
const popover = {
  position: "absolute",
  top: 32,
  left: 0,
  background: DS.card,
  border: `0.5px solid ${DS.border}`,
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(0,0,0,.15)",
  width: "min(320px, calc(100vw - 40px))",
  maxHeight: 360,
  overflowY: "auto",
  zIndex: 20,
  padding: 8,
};
const popoverItem = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "5px 8px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: 11.5,
  color: DS.marineDark,
  borderRadius: 5,
  fontFamily: DS.fontBody,
};
// B4-ext — puce d'une BRANCHE de cumul dans l'en-tête d'un encart.
const operandChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  background: "rgba(255,255,255,.75)",
  border: "0.5px solid rgba(0,0,0,.12)",
  borderRadius: 5,
  padding: "0 3px 0 6px",
  fontSize: 10,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
function opToggleBtn(color, active) {
  return {
    border: `0.5px solid ${color}`,
    background: active ? color : "transparent",
    color: active ? "#fff" : color,
    borderRadius: 5,
    padding: "0 5px",
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: 0.4,
    cursor: "pointer",
    fontFamily: DS.fontBody,
  };
}
function addCondBtn(color) {
  return {
    border: `0.5px dashed ${color}`,
    background: "transparent",
    color,
    borderRadius: 5,
    padding: "0 6px",
    fontSize: 9.5,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: DS.fontBody,
  };
}
function pillRemoveBtn(color) {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color,
    padding: 0,
    lineHeight: 0,
  };
}
function confirmBtn(color, primary) {
  return {
    background: primary ? color : "transparent",
    color: primary ? "#fff" : color,
    border: `0.5px solid ${color}`,
    borderRadius: 6,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: DS.fontBody,
  };
}
